import { useState, useContext, useRef, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { Timestamp, setDoc, deleteField } from 'firebase/firestore';
import { DocId, EntityReferences, AgentType } from '#types/System.types';
import { MessageContext } from '../../../providers/MessageContext';
import { UserContext } from '../../../providers/UserContext';
import { pendingSavesActions } from '../../../store/pendingSavesStore';
import { SidebarControlContext } from '../../../providers/SidebarControlContext';
import { CompositeFieldContext } from '../../CompositeField/CompositeFieldContext';
import { resolveContextRoutes } from '../../../services/contextRouting';
import usePendingSaves from '../../../use/usePendingSaves';
import { syncDocumentToSearch } from '../../../services/searchSyncService';
import { useEntityRecordRegistry } from '@/store/entityRecordsRegistry';
import { formStore } from '../../../store';
import { useEntityRefScope } from '../../../use/useEntityRefScope';
import { useEvaluateFormAccess } from '../../../use/data/useEvaluateFormAccess';
import { entityReferenceIndex } from '#types';
import { entityIdByReferenceKey } from '../../../use/entityIdByReferenceKey';

export type EntityRecordSelectOption = {
  docId: DocId;
  name: string;
  entityGroupIds?: string[];
};

/**
 * Standard select option shape for Ant Design (no custom props that would leak to DOM)
 */
export interface EntityRecordStandardOption {
  value: string;
  label: string;
}

/**
 * Metadata for entity record options, stored separately to avoid DOM prop warnings
 */
export interface EntityRecordOptionMetadata {
  docId: string;
  name: string;
  entityGroupIds?: string[];
  meta?: {
    searchNameLowercase?: string;
    lastModified?: any;
  };
}

/** Parameters for the useEntityRecordUI hook */
interface UseEntityRecordUIParams {
  /** Entity ID to fetch records from */
  entityId: string;
  /** Entity group ID for filtering and reference storage */
  entityGroupId?: string;
  /** When true, external onChange handler is called instead of internal save logic */
  enableExternalOnChange?: boolean;
  /** External callback invoked when selection changes, receives recordId and EntityRecordForSelect */
  externalOnChange?: (value: string | undefined, entityRecord?: EntityRecordSelectOption) => void;
  /** External value for controlled components (table cells) - used instead of reading from entityReferences */
  value?: string;
  /** When true, changes to this entity reference trigger immediate search index sync */
  triggerSearchSync?: boolean;
  /** When true, prevents any changes from being made (defense-in-depth for protected references) */
  disabled?: boolean;
}

/**
 * Return value from useEntityRecordUI hook
 * Provides state and handlers for entity record selection UI
 */
interface UseEntityRecordUIReturn {
  /** Whether the select dropdown is currently open */
  isSelectOpen: boolean;
  /** Current search query string for filtering records */
  searchQuery: string;
  /** ID of the currently selected entity record (local state) */
  recordId: string | undefined;
  /** Name of the currently selected entity record (cached from entityReferences) */
  recordName: string;
  /** Whether the component should be disabled (locked or no access) */
  isReadOnly: boolean;
  /** Whether records are currently being loaded */
  loading: boolean;
  /** Error message if record loading failed, null otherwise */
  error: string | null;
  /** Standard options for Ant Design Select (only value/label to avoid DOM prop warnings) */
  options: EntityRecordStandardOption[];
  /** Metadata map for accessing record details (docId, name, entityGroupIds) keyed by docId */
  optionsMetadata: Map<string, EntityRecordOptionMetadata>;
  /** Set search query for filtering records */
  setSearchQuery: (query: string) => void;
  /** Handler when a record is selected from dropdown */
  handleChange: (newRecordId: string | null) => void;
  /** Handler to clear the current selection */
  handleClear: () => void;
  /** Handler to open the entity record sidebar for creating/editing */
  handleOpenButtonClick: () => void;
  /** Handler when select dropdown open state changes */
  handleOpenChange: (open: boolean) => void;
}

/**
 * Custom hook for managing entity record selection UI state and handlers
 * Following the useFieldUI pattern for self-contained state management and persistence
 * 
 * Provides complete functionality for:
 * - Local state management with dirty tracking
 * - Debounced saving to entityReferences (600ms)
 * - Background record loading (loads immediately when entityId is available)
 * - Displays cached record name from entityReferences (no loading spinner if name exists)
 * - Only shows loading when recordId exists but no cached name available yet
 * - External onChange support for controlled components (table cells)
 * - Integration with pendingSavesStore
 * 
 * @param params - Configuration options for the hook
 * @returns State values and event handlers for entity record UI
 */
export const useEntityRecordUI = ({
  entityId,
  entityGroupId,
  enableExternalOnChange,
  externalOnChange,
  value,
  triggerSearchSync = false,
  disabled = false,
}: UseEntityRecordUIParams): UseEntityRecordUIReturn => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [localRecordId, setLocalRecordId] = useState<string | undefined>(undefined);
  const [localRecordName, setLocalRecordName] = useState<string>('');

  const dirtyRef = useRef<boolean>(false);
  const persistedRecordIdRef = useRef<string | undefined>(undefined);
  const lastExternalReference = useRef<typeof externalReference>(null);
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { user, hasEntityAccess } = useContext(UserContext);
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);

  const sidebarControl = useContext(SidebarControlContext);
  const compositeFieldContext = useContext(CompositeFieldContext);

  const referenceKey = entityGroupId || entityId;
  const scope = useEntityRefScope(referenceKey);
  const formAccess = useEvaluateFormAccess({
    formId: scope.formId,
    versionId: scope.versionId,
    enabled: scope.insideForm,
  });

  const insideForm = scope.insideForm;
  const insideEntity = scope.insideEntity;

  const isReadOnly = insideForm ?
    !formAccess?.canWrite :
    !hasEntityAccess || scope.entityLocked;

  const formId = scope.formId;
  const versionId = scope.versionId;
  const recordId = scope.recordId;

  const externalEntityReferences = compositeFieldContext
    ? compositeFieldContext.compositeFieldValue?.entityReferences
    : scope.allExternalReferences;

  const externalReference = compositeFieldContext
    ? (externalEntityReferences?.[referenceKey] ?? null)
    : scope.externalReference;
  
  const externalRecordId = enableExternalOnChange && value !== undefined
    ? value
    : externalReference?.entityRecordId;
  const externalRecordName = externalReference?.entityRecordName || '';

  // Only track pending saves when doing internal saves (not external mode)
  // In external mode, the parent component handles persistence
  const pendingId = usePendingSaves({
    insideForm,
    insideEntity,
    formId,
    versionId,
    entityId: scope.entityId || undefined,
    recordId,
    identifier: `entityReference:${referenceKey}`,
  });

  const {
    searchQuery,
    setSearchQuery,
    entityRecords,
    loading: searchLoading,
    error: searchError,
    registerCommonRecordUsage,
  } = useEntityRecordRegistry({
    entityId,
    entityGroupId,
  });

  const contextEntityId = scope.entityId || undefined;
  const existingEntityReferences = scope.allExternalReferences;

  /**
   * Debounced save function that persists entity reference to Firestore
   * Saves both recordId and recordName to entityReferences map
   * Following useFieldUI pattern: useMemo with dependencies to avoid stale closures
   */
  const debouncedSave = useMemo(() =>
    debounce(async (newRecordId: string | undefined, recordName: string) => {
      const now = Timestamp.now();
      const { docRef } = resolveContextRoutes({
        insideForm,
        insideEntity,
        formId,
        versionId,
        entityId: contextEntityId,
        recordId
      });

      if (!docRef) {
        console.error('[useEntityRecordUI] Cannot save entity reference: Missing required context', {
          insideForm,
          insideEntity,
          formId: formId || 'MISSING',
          versionId: versionId || 'MISSING',
          entityId: contextEntityId || 'MISSING',
          recordId: recordId || 'MISSING',
          reason: !insideForm && !insideEntity ? 'No context active' :
            insideForm && (!formId || !versionId) ? 'Form context missing IDs' :
            insideEntity && (!contextEntityId || !recordId) ? 'Entity context missing IDs' :
            'Unknown'
        });
        pendingSavesActions.removePending(pendingId);
        dirtyRef.current = false;
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
          savingTimeoutRef.current = null;
        }
        return;
      }

      const savePromise = (async () => {
        try {
          const postWriteReferences: EntityReferences = { ...(existingEntityReferences || {}) };
          if (newRecordId) {
            postWriteReferences[referenceKey] = {
              entityRecordId: newRecordId,
              entityId,
              entityRecordName: recordName,
              ...(entityGroupId ? { entityGroupId } : {}),
            };
          } else {
            delete postWriteReferences[referenceKey];
          }

          const layoutMap = entityIdByReferenceKey(formStore.state.formType.data?.defaultUILayout?.structure);
          const allEntityIdsAndGroupIds = insideForm
            ? entityReferenceIndex(postWriteReferences, (key) => layoutMap[key])
            : undefined;

          const referenceWrite = newRecordId
            ? {
                entityRecordId: newRecordId,
                entityId,
                entityRecordName: recordName,
                ...(entityGroupId ? { entityGroupId } : {}),
                meta: {
                  lastModified: now,
                  userId: user?.uid,
                  agentType: AgentType.User,
                },
              }
            : deleteField();

          const updateData = {
                entityReferences: {
                  [referenceKey]: referenceWrite,
                },
                meta: {
                  lastModified: now,
                  userId: user?.uid,
                  agentType: AgentType.User,
                },
                ...(insideForm && allEntityIdsAndGroupIds !== undefined && {
                  allEntityIdsAndGroupIds
                }),
              };

          await setDoc(docRef, updateData, { merge: true });
          persistedRecordIdRef.current = newRecordId;

          if (triggerSearchSync) {
            if (insideForm && formId && versionId) {
              try {
                void syncDocumentToSearch('formVersions', formId, versionId);
              } catch (err) {
                console.warn('[useEntityRecordUI] Failed to sync formVersion to search:', err);
              }
            }

            if (insideEntity && contextEntityId && recordId) {
              try {
                void syncDocumentToSearch('records', contextEntityId, recordId);
              } catch (err) {
                console.warn('[useEntityRecordUI] Failed to sync record to search:', err);
              }
            }
          }
        } catch (error) {
          const userMessage = 'Error saving entity reference. Changes were reverted.';
          handleFirestoreWriteError(error, '[useEntityRecordUI] debouncedSave', { userMessage, saveId: pendingId });
        } finally {
          pendingSavesActions.removePending(pendingId);
          dirtyRef.current = false;
          if (savingTimeoutRef.current) {
            clearTimeout(savingTimeoutRef.current);
            savingTimeoutRef.current = null;
          }
        }
      })();

      return savePromise;
    }, 600),
  [insideForm, insideEntity, formId, versionId, contextEntityId, recordId, referenceKey, entityId, entityGroupId, existingEntityReferences, user?.uid, handleFirestoreWriteError, triggerSearchSync, pendingId]);

  /**
   * Effect for syncing external (Firestore) state to local state
   * Following useFieldUI pattern: only updates local state when not dirty
   * This prevents overwriting user changes during active editing
   */
  useEffect(() => {
    if (externalReference !== lastExternalReference.current) {
      lastExternalReference.current = externalReference;
      if (!dirtyRef.current) {
        setLocalRecordId(externalRecordId);
        setLocalRecordName(externalRecordName);
      }
      pendingSavesActions.removePending(pendingId);
      dirtyRef.current = false;
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    }
  }, [externalReference, externalRecordId, externalRecordName, pendingId]);

  /**
   * Effect for syncing external value prop to local state (for controlled components like table cells)
   * Only runs when enableExternalOnChange is true and value changes
   * For non-table contexts: Uses externalRecordName from entityReferences (already denormalized)
   * For table cells: Looks up record name from loaded entityRecords since tables don't have entityReferences
   */
  useEffect(() => {
    if (enableExternalOnChange && value !== undefined && !dirtyRef.current) {
      setLocalRecordId(value || undefined);
      
      if (!value) {
        setLocalRecordName('');
      } else if (entityRecords.length > 0) {
        const selectedRecord = entityRecords.find((rec) => rec.docId === value);
        if (selectedRecord) {
          setLocalRecordName(selectedRecord.name || '');
        } else if (externalReference?.entityRecordName) {
          setLocalRecordName(externalReference.entityRecordName);
        }
      } else if (externalReference?.entityRecordName) {
        setLocalRecordName(externalReference.entityRecordName);
      }
    }
  }, [enableExternalOnChange, value, entityRecords, externalReference]);

  /**
   * Cleanup effect to cancel pending operations on unmount
   * Following useFieldUI pattern: cancel debounce and clear timeout
   */
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
      dirtyRef.current = false;
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    };
  }, [debouncedSave]);


  /**
   * Handler for record selection changes
   * Following useFieldUI setValue pattern:
   * - Updates local state immediately
   * - Sets dirty and saving flags
   * - Starts 10s warning timeout
   * - Either calls external handler or triggers debounced save
   * - In enableExternalOnChange mode (e.g., Tables), hands the parent lightweight
   *   record metadata only; no full-record fetch happens on selection
   */
  const handleChange = useCallback((newRecordId: string | null) => {
    // Guard: disabled references cannot be modified
    if (disabled) return;
    
    if (newRecordId !== localRecordId) {
      const selectedRecord = entityRecords.find((rec) => rec.docId === newRecordId);
      const recordName = selectedRecord?.name || 'Unnamed Record';
      
      if (newRecordId && !selectedRecord) {
        console.warn('[useEntityRecordUI] Record not found in cache:', { newRecordId, entityId, entityGroupId });
      }

      setLocalRecordId(newRecordId ?? undefined);
      setLocalRecordName(recordName);
      dirtyRef.current = true;

      // Track common record usage for optimization
      if (newRecordId) {
        registerCommonRecordUsage(entityId, newRecordId);
      }

      if (compositeFieldContext) {
        compositeFieldContext.setEntityReference(referenceKey, {
          entityRecordId: newRecordId || undefined,
          entityRecordName: newRecordId ? recordName : '',
        });
        dirtyRef.current = false;
        return;
      }

      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
      }

      savingTimeoutRef.current = setTimeout(() => {
        if (dirtyRef.current) {
          console.warn('Saving to database is taking longer than expected');
          messageApi.warning('Saving to database is taking longer than expected');
        }
      }, 10000);

      if (enableExternalOnChange && externalOnChange) {
        const recordForExternal: EntityRecordSelectOption | undefined = newRecordId && selectedRecord
          ? {
              docId: selectedRecord.docId || '',
              name: selectedRecord.name || '',
              entityGroupIds: selectedRecord.entityGroupIds,
            }
          : undefined;

        externalOnChange(newRecordId ?? undefined, recordForExternal);
        dirtyRef.current = false;
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
          savingTimeoutRef.current = null;
        }
      } else {
        pendingSavesActions.addPending(pendingId);
        debouncedSave(newRecordId ?? undefined, recordName);
      }
    }
  }, [disabled, localRecordId, entityRecords, entityId, entityGroupId, enableExternalOnChange, externalOnChange, messageApi, debouncedSave, registerCommonRecordUsage, compositeFieldContext, referenceKey, pendingId]);

  /**
   * Handler for clearing the current selection
   * Following useFieldUI setValue pattern for clearing values
   */
  const handleClear = useCallback(async () => {
    // Guard: disabled references cannot be modified
    if (disabled) return;

    setLocalRecordId(undefined);
    setLocalRecordName('');
    dirtyRef.current = true;

    if (compositeFieldContext) {
      compositeFieldContext.setEntityReference(referenceKey, {
        entityRecordId: undefined,
        entityRecordName: '',
      });
      dirtyRef.current = false;
      return;
    }

    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
    }

    savingTimeoutRef.current = setTimeout(() => {
      if (dirtyRef.current) {
        console.warn('Saving to database is taking longer than expected');
        messageApi.warning('Saving to database is taking longer than expected');
      }
    }, 10000);

    if (enableExternalOnChange && externalOnChange) {
      externalOnChange(undefined, undefined);
      dirtyRef.current = false;
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    } else {
      pendingSavesActions.addPending(pendingId);
      debouncedSave(undefined, '');
    }
  }, [disabled, enableExternalOnChange, externalOnChange, messageApi, debouncedSave, compositeFieldContext, referenceKey, pendingId]);

  const handleOpenButtonClick = useCallback(() => {
    setIsSelectOpen(false);
    
    if (sidebarControl) {
      sidebarControl.openEntitySidebar(
        entityId,
        localRecordId,
        entityGroupId,
        localRecordName,
        (callbackEntityId, callbackRecordId, callbackGroupId, callbackRecordName) => {
          const entityMatches = callbackEntityId === entityId;
          const groupMatches = callbackGroupId === entityGroupId;
          
          if (!entityMatches || !groupMatches) {
            console.error('[useEntityRecordUI] Sidebar entity/group changed, not updating original component');
            return;
          }
          
          if (!callbackRecordId) {
            // Clear selection
            handleClear();
          } else {
            // Update selection - trust callback's record name (authoritative from sidebar)
            const recordName = callbackRecordName || '';

            setLocalRecordId(callbackRecordId);
            setLocalRecordName(recordName);
            dirtyRef.current = true;

            if (compositeFieldContext) {
              compositeFieldContext.setEntityReference(referenceKey, {
                entityRecordId: callbackRecordId,
                entityRecordName: recordName,
              });
              dirtyRef.current = false;
              return;
            }

            if (savingTimeoutRef.current) {
              clearTimeout(savingTimeoutRef.current);
            }

            savingTimeoutRef.current = setTimeout(() => {
              if (dirtyRef.current) {
                console.warn('Saving to database is taking longer than expected');
                messageApi.warning('Saving to database is taking longer than expected');
              }
            }, 10000);

            if (enableExternalOnChange && externalOnChange) {
              // For external mode (tables), provide record metadata
              const recordForExternal: EntityRecordSelectOption | undefined = callbackRecordId ? {
                docId: callbackRecordId,
                name: recordName,
                entityGroupIds: callbackGroupId ? [callbackGroupId] : undefined,
              } : undefined;
              externalOnChange(callbackRecordId, recordForExternal);
              dirtyRef.current = false;
              if (savingTimeoutRef.current) {
                clearTimeout(savingTimeoutRef.current);
                savingTimeoutRef.current = null;
              }
            } else {
              // For internal mode (forms), save via debounced save
              pendingSavesActions.addPending(pendingId);
              debouncedSave(callbackRecordId, recordName);
            }
          }
        }
      );
    } else {
      console.warn('[useEntityRecordUI] SidebarControl not available');
    }
  }, [sidebarControl, entityId, localRecordId, entityGroupId, localRecordName, handleClear, enableExternalOnChange, externalOnChange, debouncedSave, messageApi, compositeFieldContext, referenceKey, pendingId]);

  const handleOpenChange = (open: boolean) => {
    setIsSelectOpen(open);
    if (open) {
      setSearchQuery('');
    }
  };

  const error = useMemo(() => {
    if (!searchError) {
      return null;
    }

    if (searchError instanceof Error) {
      const errorCode = (searchError as unknown as { code?: unknown }).code;
      if (errorCode === 'permission-denied' || errorCode === 'functions/permission-denied') {
        return "You don't have access to records.";
      }
      return searchError.message;
    }

    return String(searchError);
  }, [searchError]);

  /**
   * Compute loading state:
   * Show loading indicator when registry is loading records for this entity
   * The Select component shows a subtle spinner without hiding UI
   * This provides feedback during cache loads without blocking interaction
   */
  const loading = searchLoading;

  /**
   * Transform entity records to standard Ant Design Select options
   * Only includes value (docId) and label (name) to avoid DOM prop warnings
   */
  const standardOptions = useMemo((): EntityRecordStandardOption[] => {
    return entityRecords
      .filter((rec) => Boolean(rec.docId))
      .map((rec) => ({
        value: rec.docId as string,
        label: rec.name || 'Unnamed Record',
      }));
  }, [entityRecords]);

  /**
   * Metadata map for accessing record details in optionRender callbacks
   */
  const optionsMetadata = useMemo((): Map<string, EntityRecordOptionMetadata> => {
    return new Map(
      entityRecords
        .filter((rec) => Boolean(rec.docId))
        .map((rec) => [rec.docId as string, {
          docId: rec.docId as string,
          name: rec.name || 'Unnamed Record',
          entityGroupIds: rec.entityGroupIds,
          meta: rec.meta,
        }]),
    );
  }, [entityRecords]);

  return {
    isSelectOpen,
    searchQuery,
    recordId: localRecordId,
    recordName: localRecordName,
    isReadOnly,
    loading,
    error,
    options: standardOptions,
    optionsMetadata,
    setSearchQuery,
    handleChange,
    handleClear,
    handleOpenButtonClick,
    handleOpenChange,
  };
};

